/**
 * What the assistant knows about a customer's environment.
 *
 * WHY THIS IS VISIBLE AND EDITABLE
 * This is the honest version of "train the AI on our environment": the model is
 * not retrained, it is told the same facts every time. That design is only
 * defensible if the facts can be inspected — a technician who sees the
 * assistant behave oddly needs to look at what it was told, and an operator
 * needs to answer a client asking what is stored about them.
 *
 * A fine-tune could do none of that. Being able to read, correct and delete is
 * the advantage, so hiding the list would throw away the point.
 *
 * SCOPE MATTERS AND IS SHOWN
 * A fact with no customer applies to every client on the appliance. That is
 * right for "we never reboot before 6pm" and badly wrong for "their DC is
 * SERVER-DC01", so the distinction is on screen rather than implied.
 */
import { useCallback, useEffect, useState } from "react";
import api from "../api/client";
import ActionButton from "./ActionButton";
import { formatDateTime } from "../utils/datetime";

type Memory = {
  id: number;
  customer_id: number | null;
  scope: string;
  scope_id: number | null;
  key: string;
  value: string;
  source: string;
  confidence: number | null;
  is_active: boolean;
  use_count: number;
  last_used_at: string | null;
  updated_at: string;
};

type Customer = { id: number; name: string };

export default function AIMemoryManager() {
  const [items, setItems] = useState<Memory[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [scopeCustomer, setScopeCustomer] = useState<number | "">("");
  const [draft, setDraft] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [showRetired, setShowRetired] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);
  const [editValue, setEditValue] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await api.get("/api/v1/ai/memory", {
        params: { include_inactive: true },
      });
      setItems(r.data || []);
      setError("");
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Could not load what the assistant knows");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    api
      .get("/api/v1/customers")
      .then((r) => setCustomers((r.data || []).map((c: any) => ({ id: c.id, name: c.name }))))
      .catch(() => setCustomers([]));
  }, [load]);

  const add = async () => {
    const value = draft.trim();
    if (!value) {
      setError("Type the fact you want the assistant to remember.");
      return;
    }
    setError("");
    try {
      await api.post("/api/v1/ai/memory", {
        value,
        customer_id: scopeCustomer === "" ? null : Number(scopeCustomer),
      });
      setDraft("");
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Could not save that");
    }
  };

  const saveEdit = async (m: Memory) => {
    try {
      await api.put(`/api/v1/ai/memory/${m.id}`, { value: editValue.trim() });
      setEditing(null);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Could not update that");
    }
  };

  const toggle = async (m: Memory) => {
    try {
      await api.put(`/api/v1/ai/memory/${m.id}`, { is_active: !m.is_active });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Could not update that");
    }
  };

  const remove = async (m: Memory) => {
    if (
      !window.confirm(
        `Delete this permanently?\n\n"${m.value}"\n\nIf you only want the assistant to ` +
          `stop using it, retire it instead — that keeps the record of what it once believed.`,
      )
    )
      return;
    try {
      await api.delete(`/api/v1/ai/memory/${m.id}`);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Could not delete that");
    }
  };

  const nameFor = (id: number | null) =>
    id === null ? "All customers" : customers.find((c) => c.id === id)?.name || `Customer ${id}`;

  const visible = showRetired ? items : items.filter((m) => m.is_active);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
      <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
        What the assistant knows
      </h2>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
        Facts about your customers' environments, included in every AI request for that
        customer. This is how the assistant learns your estate — the model itself is not
        retrained, it is told these facts each time, which means you can see exactly what
        it believes and correct it here.
      </p>

      {error && (
        <div className="mb-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm rounded-lg px-3 py-2">
          {error}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <select
          value={scopeCustomer}
          onChange={(e) => setScopeCustomer(e.target.value === "" ? "" : Number(e.target.value))}
          className="border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm sm:w-56"
          title="A fact with no customer applies to every client on this appliance"
        >
          <option value="">All customers (house rule)</option>
          {customers.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") add();
          }}
          placeholder="e.g. SERVER-DC01 is the domain controller — never reboot it in business hours"
          className="flex-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
        />
        <ActionButton
          onAction={add}
          variant="raw"
          className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2 rounded-lg"
          pendingLabel="Saving…"
        >
          Remember
        </ActionButton>
      </div>

      {loading && <div className="text-xs text-gray-400 dark:text-gray-500">Loading…</div>}
      {!loading && visible.length === 0 && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Nothing recorded yet. Add the things you find yourself telling the assistant twice.
        </p>
      )}

      <ul className="space-y-1.5">
        {visible.map((m) => (
          <li
            key={m.id}
            className={`group flex items-start gap-3 rounded-lg border px-3 py-2 ${
              m.is_active
                ? "border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/40"
                : "border-dashed border-gray-200 dark:border-gray-700 opacity-60"
            }`}
          >
            <div className="min-w-0 flex-1">
              {editing === m.id ? (
                <div className="flex items-center gap-2">
                  <input
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveEdit(m);
                      if (e.key === "Escape") setEditing(null);
                    }}
                    autoFocus
                    className="flex-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm"
                  />
                  <button
                    onClick={() => saveEdit(m)}
                    className="text-[11px] text-blue-600 dark:text-blue-400"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => setEditing(null)}
                    className="text-[11px] text-gray-500"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setEditing(m.id);
                    setEditValue(m.value);
                  }}
                  className="text-left text-sm text-gray-800 dark:text-gray-200 w-full"
                  title="Click to edit"
                >
                  {m.value}
                </button>
              )}
              <div className="text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-1.5 flex-wrap mt-0.5">
                <span
                  className={
                    m.customer_id === null
                      ? "rounded-full bg-indigo-100 dark:bg-indigo-500/20 text-indigo-800 dark:text-indigo-300 px-1.5 py-0.5 font-medium"
                      : ""
                  }
                >
                  {nameFor(m.customer_id)}
                </span>
                {m.scope === "device" && (
                  <>
                    <span>·</span>
                    <span>device {m.scope_id}</span>
                  </>
                )}
                {m.source !== "manual" && (
                  <>
                    <span>·</span>
                    {/* A guess must not read like an established fact. */}
                    <span className="rounded-full bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-300 px-1.5 py-0.5">
                      inferred
                    </span>
                  </>
                )}
                <span>·</span>
                <span>used {m.use_count}×</span>
                {m.last_used_at && (
                  <>
                    <span>·</span>
                    <span>last {formatDateTime(m.last_used_at)}</span>
                  </>
                )}
                {!m.is_active && (
                  <span className="rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-1.5 py-0.5">
                    Retired
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => toggle(m)}
              className="text-[11px] text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 px-2 py-1 shrink-0"
            >
              {m.is_active ? "Retire" : "Restore"}
            </button>
            <button
              type="button"
              onClick={() => remove(m)}
              aria-label="Delete this memory"
              className="text-xs text-gray-400 hover:text-red-600 dark:hover:text-red-400 px-1.5 py-1 shrink-0 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
            >
              ✕
            </button>
          </li>
        ))}
      </ul>

      {items.some((m) => !m.is_active) && (
        <button
          type="button"
          onClick={() => setShowRetired((v) => !v)}
          className="mt-3 text-xs text-gray-500 dark:text-gray-400 hover:underline"
        >
          {showRetired ? "Hide retired" : "Show retired"}
        </button>
      )}
    </div>
  );
}
